import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ExamEventTrackerService } from '../services/exam-event-tracker.service';
import { MockTestSecurityService } from './mock-test-security.service';
import { SecurityViolationType } from './mock-test-security.model';

/** Disconnects in one attempt before REPEATED_NETWORK_DISRUPTION_DETECTED fires (and again at each multiple). */
const NETWORK_DISRUPTION_THRESHOLD = 3;
const EXTENSION_URL_PATTERN = /^(chrome|moz|safari-web|ms-browser)-extension:\/\//i;
/**
 * GPU renderer strings of hypervisors and software rasterizers. SwiftShader /
 * llvmpipe also show up on real machines with hardware acceleration off, so
 * this is reported as a signal with the renderer attached, not a verdict.
 */
const VIRTUAL_RENDERER_PATTERN = /virtualbox|vmware|parallels|hyper-v|qemu|virgl|llvmpipe|swiftshader|microsoft basic render/i;

// Browser APIs not (or not fully) in lib.dom — all feature-detected at runtime.
interface NetworkInformationLike extends EventTarget {
  type?: string;
}

interface BatteryManagerLike extends EventTarget {
  charging: boolean;
}

type ExtendedScreen = Screen & EventTarget & { isExtended?: boolean };

type DeviceOrientation = 'PORTRAIT' | 'LANDSCAPE';

/** lock() isn't in lib.dom; Android Chrome supports it in fullscreen, iOS Safari not at all. */
type LockableScreenOrientation = ScreenOrientation & { lock?: (orientation: 'landscape') => Promise<void> };

export type SecurityMonitoringStopReason = 'submitted' | 'exited' | 'tab_closed' | 'navigated_away';

/**
 * Environment telemetry for the running exam — the Charaka security-catalog
 * events a browser can actually observe (displays, orientation, network
 * type, charger, audio/peripheral devices, page lifecycle, external links,
 * navigation attempts, injected scripts, virtualized/automated browsers).
 * Reporting only — nothing here warns or ends the exam; the one exception is
 * locking the screen to landscape where the browser allows it. Catalog events
 * that need a native/managed client (lockdown, kiosk, microphone streams,
 * notification shade, device lock, ...) aren't observable from a web page
 * and are intentionally not emitted.
 */
@Injectable({ providedIn: 'root' })
export class ExamEnvironmentMonitorService {
  private readonly router = inject(Router);
  private readonly eventTracker = inject(ExamEventTrackerService);
  private readonly securityService = inject(MockTestSecurityService);

  private isMonitoring = false;
  private disconnectCount = 0;
  private wasScreenExtended = false;
  private screenSize = { width: 0, height: 0 };
  private orientation: DeviceOrientation | null = null;
  private portraitQuery: MediaQueryList | null = null;
  private networkType: string | null = null;
  private mediaDeviceKeys = new Map<string, MediaDeviceKind>();
  private battery: BatteryManagerLike | null = null;
  private mutationObserver: MutationObserver | null = null;
  private routerSubscription: Subscription | null = null;
  private originalWindowOpen: typeof window.open | null = null;
  private readonly reportedScriptSources = new Set<string>();

  private readonly boundOnScreenChange = this.onScreenChange.bind(this);
  private readonly boundOnWindowResize = this.checkDisplayConfiguration.bind(this);
  private readonly boundOnOrientationChange = this.onOrientationChange.bind(this);
  private readonly boundOnNetworkChange = this.onNetworkChange.bind(this);
  private readonly boundOnOffline = this.onOffline.bind(this);
  private readonly boundOnChargingChange = this.onChargingChange.bind(this);
  private readonly boundOnMediaDeviceChange = this.onMediaDeviceChange.bind(this);
  private readonly boundOnGamepadConnected = this.onGamepadChange.bind(this, true);
  private readonly boundOnGamepadDisconnected = this.onGamepadChange.bind(this, false);
  private readonly boundOnFreeze = this.onFreeze.bind(this);
  private readonly boundOnResume = this.onResume.bind(this);
  private readonly boundOnLinkClick = this.onLinkClick.bind(this);
  private readonly boundOnBeforeUnload = this.onBeforeUnload.bind(this);
  private readonly boundOnFullscreenChange = this.lockLandscape.bind(this);

  start(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.disconnectCount = 0;
    this.reportedScriptSources.clear();
    this.eventTracker.track('SECURITY_MONITORING_STARTED');

    this.reportInitialEnvironment();
    this.watchDisplays();
    this.watchNetwork();
    this.watchDevices();
    this.watchPageLifecycle();
    this.watchNavigation();
    this.watchInjectedScripts();
  }

  stop(reason: SecurityMonitoringStopReason): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;
    this.eventTracker.track('SECURITY_MONITORING_STOPPED', { reason });

    const extendedScreen = screen as ExtendedScreen;
    extendedScreen.removeEventListener?.('change', this.boundOnScreenChange);
    window.removeEventListener('resize', this.boundOnWindowResize);
    screen.orientation?.removeEventListener('change', this.boundOnOrientationChange);
    document.removeEventListener('fullscreenchange', this.boundOnFullscreenChange);
    this.unlockOrientation();
    this.portraitQuery?.removeEventListener('change', this.boundOnOrientationChange);
    this.portraitQuery = null;
    this.getConnection()?.removeEventListener('change', this.boundOnNetworkChange);
    window.removeEventListener('offline', this.boundOnOffline);
    this.battery?.removeEventListener('chargingchange', this.boundOnChargingChange);
    this.battery = null;
    navigator.mediaDevices?.removeEventListener('devicechange', this.boundOnMediaDeviceChange);
    window.removeEventListener('gamepadconnected', this.boundOnGamepadConnected);
    window.removeEventListener('gamepaddisconnected', this.boundOnGamepadDisconnected);
    document.removeEventListener('freeze', this.boundOnFreeze);
    document.removeEventListener('resume', this.boundOnResume);
    document.removeEventListener('click', this.boundOnLinkClick, true);
    document.removeEventListener('auxclick', this.boundOnLinkClick, true);
    window.removeEventListener('beforeunload', this.boundOnBeforeUnload);

    if (this.originalWindowOpen) {
      window.open = this.originalWindowOpen;
      this.originalWindowOpen = null;
    }

    this.routerSubscription?.unsubscribe();
    this.routerSubscription = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.mediaDeviceKeys.clear();
  }

  // ---------------------------------------------------------------------------
  // One-off checks at exam start

  private reportInitialEnvironment(): void {
    // Automation-driven browser (Selenium, Puppeteer, Playwright, ...).
    if (navigator.webdriver) {
      this.eventTracker.track('UNAUTHORIZED_SOFTWARE_SUSPECTED', { processName: 'webdriver' });
    }

    const renderer = this.getGpuRenderer();

    if (renderer && VIRTUAL_RENDERER_PATTERN.test(renderer)) {
      this.eventTracker.track('VIRTUAL_ENVIRONMENT_DETECTED', { renderer });
    }
  }

  private getGpuRenderer(): string | null {
    try {
      const gl = document.createElement('canvas').getContext('webgl');

      if (!gl) {
        return null;
      }

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const renderer = gl.getParameter(debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER);
      gl.getExtension('WEBGL_lose_context')?.loseContext();

      return typeof renderer === 'string' ? renderer : null;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Displays & orientation

  private watchDisplays(): void {
    const extendedScreen = screen as ExtendedScreen;

    this.screenSize = { width: screen.width, height: screen.height };
    this.wasScreenExtended = Boolean(extendedScreen.isExtended);

    // screen.isExtended (Chromium) only says "more than one"; the exact count
    // needs the Window Management permission, which isn't worth a prompt here.
    if (this.wasScreenExtended) {
      this.eventTracker.track('MULTIPLE_DISPLAYS_DETECTED', { displayCount: 2 });
    }

    extendedScreen.addEventListener?.('change', this.boundOnScreenChange);
    window.addEventListener('resize', this.boundOnWindowResize);

    // The app only lets an exam start in landscape; a tablet turned to
    // portrait mid-exam is reported here (and covered by the rotate prompt).
    this.orientation = this.readOrientation();

    // Lock only holds in fullscreen, so re-apply it each time the exam
    // re-enters fullscreen.
    this.lockLandscape();
    document.addEventListener('fullscreenchange', this.boundOnFullscreenChange);

    if (screen.orientation) {
      screen.orientation.addEventListener('change', this.boundOnOrientationChange);
    } else {
      // Older iOS Safari has no screen.orientation.
      this.portraitQuery = window.matchMedia('(orientation: portrait)');
      this.portraitQuery.addEventListener('change', this.boundOnOrientationChange);
    }
  }

  private onScreenChange(): void {
    const isExtended = Boolean((screen as ExtendedScreen).isExtended);

    if (isExtended !== this.wasScreenExtended) {
      this.wasScreenExtended = isExtended;
      this.eventTracker.track(isExtended ? 'EXTERNAL_DISPLAY_CONNECTED' : 'EXTERNAL_DISPLAY_DISCONNECTED');
    }

    this.checkDisplayConfiguration();
  }

  /**
   * Resolution / scaling changes only — window/viewport resizes are
   * SCREEN_RESIZED, reported by the question paper.
   */
  private checkDisplayConfiguration(): void {
    const { width, height } = screen;

    if (width === this.screenSize.width && height === this.screenSize.height) {
      return;
    }

    this.screenSize = { width, height };
    this.eventTracker.track('DISPLAY_CONFIGURATION_CHANGED', {
      width,
      height,
      devicePixelRatio: window.devicePixelRatio,
    });
  }

  private onOrientationChange(): void {
    const orientation = this.readOrientation();

    // 180° turns (landscape-primary → landscape-secondary) aren't a change.
    if (orientation === this.orientation) {
      return;
    }

    this.orientation = orientation;
    this.eventTracker.track('DEVICE_ORIENTATION_CHANGED', { orientation });
  }

  /**
   * Stops the device rotating to portrait at all (Android Chrome, fullscreen
   * only). Where it isn't supported the rotation still happens, and the app
   * covers the exam with a "portrait mode not allowed" warning instead.
   */
  private lockLandscape(): void {
    const orientation = screen.orientation as LockableScreenOrientation | undefined;

    if (!document.fullscreenElement || !orientation?.lock) {
      return;
    }

    orientation.lock('landscape').catch(() => {
      // Not supported on this device/browser — the warning overlay covers it.
    });
  }

  private unlockOrientation(): void {
    try {
      screen.orientation?.unlock();
    } catch {
      // Nothing was locked.
    }
  }

  private readOrientation(): DeviceOrientation {
    const type = screen.orientation?.type;
    const isPortrait = type ? type.startsWith('portrait') : window.matchMedia('(orientation: portrait)').matches;
    return isPortrait ? 'PORTRAIT' : 'LANDSCAPE';
  }

  // ---------------------------------------------------------------------------
  // Network

  private getConnection(): NetworkInformationLike | null {
    return (navigator as Navigator & { connection?: NetworkInformationLike }).connection ?? null;
  }

  private watchNetwork(): void {
    const connection = this.getConnection();

    this.networkType = this.normalizeNetworkType(connection?.type);
    connection?.addEventListener('change', this.boundOnNetworkChange);
    window.addEventListener('offline', this.boundOnOffline);
  }

  // `type` (wifi / cellular / ethernet ...) is only exposed on some platforms
  // (mainly Android Chrome); `effectiveType` changes are speed, not interface,
  // so they're deliberately not reported as a type change.
  private onNetworkChange(): void {
    const currentType = this.normalizeNetworkType(this.getConnection()?.type);

    if (!currentType || currentType === this.networkType) {
      return;
    }

    this.eventTracker.track('NETWORK_TYPE_CHANGED', {
      previousType: this.networkType,
      currentType,
    });
    this.networkType = currentType;
  }

  private normalizeNetworkType(type: string | undefined): string | null {
    return type ? type.toUpperCase() : null;
  }

  // NETWORK_DISCONNECTED itself is reported by the question paper; this only
  // escalates once disconnects pile up.
  private onOffline(): void {
    this.disconnectCount += 1;

    if (this.disconnectCount % NETWORK_DISRUPTION_THRESHOLD === 0) {
      this.eventTracker.track('REPEATED_NETWORK_DISRUPTION_DETECTED', { disconnectCount: this.disconnectCount });
    }
  }

  // ---------------------------------------------------------------------------
  // Charger, audio devices, peripherals

  private watchDevices(): void {
    const getBattery = (navigator as Navigator & { getBattery?: () => Promise<BatteryManagerLike> }).getBattery;

    getBattery?.call(navigator)
      .then((battery) => {
        if (!this.isMonitoring) {
          return;
        }

        this.battery = battery;
        battery.addEventListener('chargingchange', this.boundOnChargingChange);
      })
      .catch(() => undefined);

    const mediaDevices = navigator.mediaDevices;

    if (mediaDevices) {
      void this.readMediaDevices().then((devices) => {
        if (!this.isMonitoring) {
          return;
        }

        this.mediaDeviceKeys = devices;
        mediaDevices.addEventListener('devicechange', this.boundOnMediaDeviceChange);
      });
    }

    window.addEventListener('gamepadconnected', this.boundOnGamepadConnected);
    window.addEventListener('gamepaddisconnected', this.boundOnGamepadDisconnected);
  }

  private onChargingChange(): void {
    this.eventTracker.track(this.battery?.charging ? 'CHARGER_CONNECTED' : 'CHARGER_DISCONNECTED');
  }

  /**
   * Without camera/mic permission browsers expose at most one anonymous
   * entry per kind, so only a kind appearing or disappearing is visible;
   * with permission, individual devices (and their labels) are.
   */
  private async readMediaDevices(): Promise<Map<string, MediaDeviceKind>> {
    const keys = new Map<string, MediaDeviceKind>();

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      devices.forEach((device, index) => {
        keys.set(`${device.kind}|${device.deviceId || `anonymous-${index}`}|${device.label}`, device.kind);
      });
    } catch {
      // enumerateDevices can reject in insecure contexts; nothing to watch then.
    }

    return keys;
  }

  private async onMediaDeviceChange(): Promise<void> {
    const previous = this.mediaDeviceKeys;
    const current = await this.readMediaDevices();

    if (!this.isMonitoring) {
      return;
    }

    this.mediaDeviceKeys = current;

    current.forEach((kind, key) => {
      if (!previous.has(key)) {
        this.reportMediaDevice(kind, key, true);
      }
    });

    previous.forEach((kind, key) => {
      if (!current.has(key)) {
        this.reportMediaDevice(kind, key, false);
      }
    });
  }

  private reportMediaDevice(kind: MediaDeviceKind, key: string, connected: boolean): void {
    if (kind === 'videoinput') {
      this.eventTracker.track(connected ? 'EXTERNAL_PERIPHERAL_CONNECTED' : 'EXTERNAL_PERIPHERAL_DISCONNECTED', {
        peripheralType: 'camera',
      });
      return;
    }

    const label = key.split('|').slice(2).join('|');
    const deviceType = this.classifyAudioDevice(kind, label);
    this.eventTracker.track('AUDIO_DEVICE_CHANGED', {
      deviceType,
      state: connected ? 'connected' : 'disconnected',
    });

    // A microphone or headset appearing/disappearing mid-test is a violation;
    // plain speaker changes are not.
    if (deviceType !== 'speaker') {
      this.securityService.reportViolation(SecurityViolationType.MICROPHONE_ATTEMPT, {
        source: 'device_change',
        deviceType,
        state: connected ? 'connected' : 'disconnected',
      });
    }
  }

  private classifyAudioDevice(kind: MediaDeviceKind, label: string): string {
    if (/bluetooth|airpods|buds|hands-free/i.test(label)) {
      return 'bluetooth_headphones';
    }

    if (/headphone|headset|earphone|earbud/i.test(label)) {
      return 'headphones';
    }

    return kind === 'audioinput' ? 'microphone' : 'speaker';
  }

  private onGamepadChange(connected: boolean): void {
    this.eventTracker.track(connected ? 'EXTERNAL_PERIPHERAL_CONNECTED' : 'EXTERNAL_PERIPHERAL_DISCONNECTED', {
      peripheralType: 'gamepad',
    });
  }

  // ---------------------------------------------------------------------------
  // Page lifecycle — the browser freezing/resuming the whole page (tab
  // discarded to background, mobile app switch), a deeper state than the
  // BROWSER_TAB_HIDDEN/VISIBLE the question paper reports.

  private watchPageLifecycle(): void {
    document.addEventListener('freeze', this.boundOnFreeze);
    document.addEventListener('resume', this.boundOnResume);
  }

  private onFreeze(): void {
    this.eventTracker.track('EXAM_APP_BACKGROUND');
  }

  private onResume(): void {
    this.eventTracker.track('EXAM_APP_FOREGROUND');
  }

  // ---------------------------------------------------------------------------
  // Leaving the exam: external links, window.open, reload/close, back/forward

  private watchNavigation(): void {
    document.addEventListener('click', this.boundOnLinkClick, true);
    document.addEventListener('auxclick', this.boundOnLinkClick, true);
    window.addEventListener('beforeunload', this.boundOnBeforeUnload);

    this.originalWindowOpen = window.open;
    window.open = ((url?: string | URL) => {
      this.eventTracker.track('EXTERNAL_LINK_OPEN_ATTEMPTED', { targetUrl: url ? String(url) : null });
      return null;
    }) as typeof window.open;

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && event.navigationTrigger === 'popstate') {
        this.eventTracker.track('UNAUTHORIZED_NAVIGATION_ATTEMPTED', {
          targetUrl: event.url,
          trigger: 'browser_history',
        });
      }
    });
  }

  /** Links inside question content open nothing during the exam; the attempt is reported. */
  private onLinkClick(event: MouseEvent): void {
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;

    if (!anchor) {
      return;
    }

    const targetUrl = anchor.href;
    const opensElsewhere = anchor.target === '_blank' || event.button === 1 || event.ctrlKey || event.metaKey || event.shiftKey;
    let isExternal = true;

    try {
      isExternal = new URL(targetUrl, window.location.href).origin !== window.location.origin;
    } catch {
      // Unparseable href — treat as external.
    }

    if (!isExternal && !opensElsewhere) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.eventTracker.track('EXTERNAL_LINK_OPEN_ATTEMPTED', { targetUrl });
  }

  // Reload, tab close, typing a URL — the browser doesn't reveal the target.
  private onBeforeUnload(): void {
    this.eventTracker.track('UNAUTHORIZED_NAVIGATION_ATTEMPTED', { targetUrl: null, trigger: 'page_unload' });
  }

  // ---------------------------------------------------------------------------
  // Scripts injected into the exam page after it loaded

  private watchInjectedScripts(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => this.inspectAddedNode(node));
      }
    });

    this.mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  /**
   * The app's own code never adds <script> tags at runtime (lazy chunks load
   * through import()), so an inline or foreign-origin script — or anything
   * loaded from an extension URL — came from outside the app.
   */
  private inspectAddedNode(node: Node): void {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    const source = node.getAttribute('src') ?? node.getAttribute('href') ?? '';

    if (source && EXTENSION_URL_PATTERN.test(source)) {
      this.reportScriptSource('BROWSER_EXTENSION_CONFLICT_DETECTED', source, { source, element: node.tagName.toLowerCase() });
      return;
    }

    if (node.tagName !== 'SCRIPT') {
      return;
    }

    if (!source) {
      this.reportScriptSource('UNAUTHORIZED_SCRIPT_MODIFICATION_SUSPECTED', 'inline', { src: 'inline' });
      return;
    }

    try {
      if (new URL(source, window.location.href).origin === window.location.origin) {
        return;
      }
    } catch {
      // Unparseable src — report it.
    }

    this.reportScriptSource('UNAUTHORIZED_SCRIPT_MODIFICATION_SUSPECTED', source, { src: source });
  }

  private reportScriptSource(
    eventType: 'BROWSER_EXTENSION_CONFLICT_DETECTED' | 'UNAUTHORIZED_SCRIPT_MODIFICATION_SUSPECTED',
    key: string,
    payload: Record<string, unknown>,
  ): void {
    const dedupeKey = `${eventType}|${key}`;

    if (this.reportedScriptSources.has(dedupeKey)) {
      return;
    }

    this.reportedScriptSources.add(dedupeKey);
    this.eventTracker.track(eventType, payload);
  }
}
